import { useState } from 'react';

import {
  SolutionSessionMessage,
  SolutionSessionMessageType,
} from '@backrs_ui/grow-ui/src/models/models';

import { Box, Theme, SxProps } from '@mui/material';

import { MultiFreeform } from './MultiFreeform';
import { OptionSelect } from './OptionSelect';
import { EducationQuestion } from './ResumeBuilder/EducationQuestion';
import { EngagementQuestion } from './ResumeBuilder/EngagementQuestion';
import { WorkExperienceQuestion } from './ResumeBuilder/WorkExperienceQuestion';
import { SkipButton } from './SkipButton';
import { SubmitButton } from './SubmitButton';
import { FileUploadQuestion } from '../FileUploadQuestion';

const TYPES_WITH_SUBMIT = ['multi_select', 'multi_freeform', 'multi_numeric'];

const TYPES_WITHOUT_SKIP = ['education', 'work_experience', 'engagement'];

type QuestionProps = Readonly<{
  type: SolutionSessionMessageType;
  required: boolean;
  userId: number;
  chatMode?: boolean;

  options?: string[];

  message?: SolutionSessionMessage;

  sendQAMessage: (message: string, LLMInput?: string) => void;
}>;

export const Question = ({
  type,
  required,
  userId,
  chatMode,

  options,

  message,

  sendQAMessage,
}: QuestionProps) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

  const showSkip =
    !chatMode && !TYPES_WITHOUT_SKIP.includes(type as (typeof TYPES_WITHOUT_SKIP)[number]);
  const showSubmit = TYPES_WITH_SUBMIT.includes(type as (typeof TYPES_WITH_SUBMIT)[number]);

  const createQuestionComponent = () => {
    switch (type) {
      case 'freeform':
      case 'numeric':
        return null;
      case 'single_select':
      case 'single_select_described':
        return (
          <OptionSelect
            variant={{
              type: 'single_select',
              options: options,
              sendQAMessage: sendQAMessage,
            }}
          />
        );
      case 'multi_select':
        return (
          <OptionSelect
            variant={{
              type: 'multi_select',
              options: options,
              sendQAMessage: sendQAMessage,
              selectedOptions: selectedOptions,
              setSelectedOptions: setSelectedOptions,
            }}
          />
        );
      case 'file_upload':
        return message ? (
          <FileUploadQuestion userId={userId} sendQAMessage={sendQAMessage} message={message} />
        ) : null;
      case 'multi_freeform':
        return <MultiFreeform options={options} sendQAMessage={sendQAMessage} />;
      case 'multi_numeric':
        return <MultiFreeform options={options} sendQAMessage={sendQAMessage} numericOnly={true} />;
      case 'education':
        return (
          <EducationQuestion
            sendQAMessage={sendQAMessage}
            questionNumber={Number(message?.questionField?.split('.')[1])}
            message={message}
          />
        );
      case 'work_experience':
        return (
          <WorkExperienceQuestion
            sendQAMessage={sendQAMessage}
            questionNumber={Number(message?.questionField?.split('.')[1])}
            message={message}
          />
        );
      case 'engagement':
        return (
          <EngagementQuestion
            sendQAMessage={sendQAMessage}
            questionNumber={Number(message?.questionField?.split('.')[1])}
            message={message}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={containerSx}>
      {type && <Box sx={questionContainerSx}>{createQuestionComponent()}</Box>}
      <Box sx={submitAndSkipContainerSx}>
        {showSubmit ? (
          <SubmitButton
            type={type}
            sendQAMessage={sendQAMessage}
            required={required}
            selectedOptions={selectedOptions}
            setSelectedOptions={setSelectedOptions}
          />
        ) : (
          <Box />
        )}
        {showSkip && <SkipButton isRequired={required} sendQAMessage={sendQAMessage} />}
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = {
  width: '100%',
};

const questionContainerSx: SxProps<Theme> = ({ backrs }) => ({
  marginTop: backrs.spacing['spacing-md'],
});

const submitAndSkipContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexDirection: 'row',
  marginTop: backrs.spacing['spacing-xl'],
});
